import { useRef, useEffect, useCallback } from 'react';
import {
  recordToolOpen,
  recordStepCompleted,
  recordReportGenerated,
  recordReportShared,
  recordExportPdf,
  recordTimeSpent,
} from '../lib/agents/usageTracking';

/**
 * Hook to track tool usage for the current agent. Call once per tool page.
 * @param {{ uid: string, email: string }} agentProfile - from useAgentAuth().agentProfile
 * @param {string} toolId
 * @returns {{ recordStep, recordReport, recordShare, recordPdfExport, recordTimeSpent }}
 */
export function useAgentToolUsage(agentProfile, toolId) {
  const openRecorded = useRef(false);
  const startTime = useRef(null);
  const userId = agentProfile?.uid;
  const userEmail = agentProfile?.email;

  useEffect(() => {
    if (!userId || !userEmail || !toolId) return;
    startTime.current = Date.now();
    recordToolOpen(userId, userEmail, toolId);
    openRecorded.current = true;
    return () => {
      if (openRecorded.current && startTime.current) {
        const seconds = Math.round((Date.now() - startTime.current) / 1000);
        if (seconds > 0) {
          recordTimeSpent(userId, userEmail, toolId, seconds);
        }
      }
    };
  }, [userId, userEmail, toolId]);

  const recordStep = useCallback(
    (stepName, payload) => {
      if (!userId || !userEmail || !toolId) return;
      recordStepCompleted(userId, userEmail, toolId, stepName, payload);
    },
    [userId, userEmail, toolId]
  );

  const recordReport = useCallback(
    (payload) => {
      if (!userId || !userEmail || !toolId) return;
      recordReportGenerated(userId, userEmail, toolId, payload);
    },
    [userId, userEmail, toolId]
  );

  const recordShare = useCallback(
    (payload) => {
      if (!userId || !userEmail || !toolId) return;
      recordReportShared(userId, userEmail, toolId, payload);
    },
    [userId, userEmail, toolId]
  );

  const recordPdfExport = useCallback(
    (payload) => {
      if (!userId || !userEmail || !toolId) return;
      recordExportPdf(userId, userEmail, toolId, payload);
    },
    [userId, userEmail, toolId]
  );

  const recordTimeSpentManual = useCallback(
    (seconds, payload) => {
      if (!userId || !userEmail || !toolId) return;
      recordTimeSpent(userId, userEmail, toolId, seconds, payload);
    },
    [userId, userEmail, toolId]
  );

  return {
    recordStep,
    recordReport,
    recordShare,
    recordPdfExport,
    recordTimeSpent: recordTimeSpentManual,
  };
}
